import React from "react";
// import "./App.css";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Backdrop,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  InputAdornment,
} from "@mui/material";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import AssignRouteModal, {
  AssignRouteFormValues,
} from "app/components/Modal/AssignRouteModal";
import {
  DeliveryBoardColumn,
  EMPTY_SELECTED_IDS,
  BoardStage,
} from "app/components/Dashboard/DeliveryBoardColumn";
import { DeliveryPickingCard } from "app/components/Dashboard/DeliveryPickingCard";
import MoveDeliveryAreaModal, {
  PendingDeliveryAreaMove,
} from "app/components/Modal/MoveDeliveryAreaModal";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  AuthExpiredError,
  AuthData,
  clearAuthData,
  getOdooApiHeaders,
  getStoredAuthData,
  isEmbeddedMode,
  recoverAuth,
  subscribeToAuthUpdates,
  odooApiFetch,
} from "app/utils/auth";
import { useAuthContext } from "app/components/Auth/AuthContext";
import {
  buildDeliveryPartnersUrl,
  extractOdooResults,
  serializeOdooFilters,
  splitDeliveryPartners,
  DeliveryPartner,
  updatePickingDeliveryArea,
  assignPickingsToRoute,
} from "app/utils/odooApi";
import { handleApiError, notifyApiError } from "app/utils/apiErrorHandler";
import { buildAssignPickingsPayload } from "app/utils/assignPickings";
import { BRANDING } from "app/utils/branding";
import { normalizeBoardStages } from "app/utils/boardStages";
import {
  cloneBoardStages,
  columnDroppableId,
  findContainerForOver,
  movePickingBetweenStages,
  stageIdsEqual,
} from "app/utils/kanbanDnD";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  getUserTimezone,
  localDateRangeToUtc,
} from "app/utils/timezone";
import {
  DATE_RANGE_PRESET_OPTIONS,
  DateRangePresetId,
  getDateRangeForPreset,
} from "app/utils/dateRangePresets";
import {
  getInitialDeliveryPartnersState,
  saveDeliveryPartnersToStorage,
} from "app/utils/deliveryPartnersCache";
import {
  FILTER_FIELD_SX,
  HEADER_PAPER_SX,
  HEADER_TOOLBAR_SX,
  HEADER_ICON_BUTTON_SX,
} from "app/components/Layout/filterToolbarStyles";
import { KnowledgeButton } from "app/components/Knowledge/KnowledgeButton";
import { KnowledgePanel } from "app/components/Knowledge/KnowledgePanel";

const DATE_DISPLAY_FORMAT = "MM/DD/YYYY";

const buildPickingFilters = (options: {
  startDate: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  timezone: string;
}) => {
  const endDate = options.endDate ?? options.startDate;
  const startRange = localDateRangeToUtc(options.startDate, options.timezone);
  const endRange = localDateRangeToUtc(endDate, options.timezone);

  return [
    ["company_id", "=", 1],
    ["state", "=", "done"],
    ["scheduled_date", ">=", startRange.startUtc],
    ["scheduled_date", "<=", endRange.endUtc],
    ["delivery_area", "!=", false],
  ];
};

type ScrollThumbMetrics = {
  widthPct: number;
  leftPct: number;
  hasOverflow: boolean;
};

const DEFAULT_SCROLL_THUMB: ScrollThumbMetrics = {
  widthPct: 100,
  leftPct: 0,
  hasOverflow: false,
};

type AssignmentFilter = "unassigned" | "assigned" | "all";

const extractPickingResults = extractOdooResults;

const getOdooRelationId = (value: unknown): number | null => {
  if (value == null || value === false) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "number") {
    return value[0];
  }
  if (typeof value === "object" && typeof (value as { id?: unknown }).id === "number") {
    return (value as { id: number }).id;
  }
  return null;
};

const buildPickingOrdersTableData = (
  areas: any[],
  pickingOrdersValue: any[]
) => {
  const orders = Array.isArray(pickingOrdersValue) ? pickingOrdersValue : [];
  const ordersByArea = new Map<number | string, any[]>();

  for (const order of orders) {
    const areaId = getOdooRelationId(order?.delivery_area) ?? "none";
    const bucket = ordersByArea.get(areaId);
    if (bucket) {
      bucket.push(order);
    } else {
      ordersByArea.set(areaId, [order]);
    }
  }

  const data = areas.map((dItem: any) => ({
    title: dItem?.name,
    items: ordersByArea.get(dItem?.id) ?? [],
    color: dItem?.color,
    id: dItem?.id,
  }));

  data.push({
    title: "None",
    items: ordersByArea.get("none") ?? [],
    color: "#808080",
    id: "none",
  });

  return data;
};

const isPickingAssigned = (item: any): boolean => {
  const driver = item?.driver_id;
  if (!driver || driver === false) {
    return false;
  }
  if (typeof driver === "object") {
    return Boolean(driver.id);
  }
  return Boolean(driver);
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const embedded = isEmbeddedMode();
  const contextAuth = useAuthContext();

  const [authData, setAuthData] = React.useState<AuthData>(
    () => contextAuth || getStoredAuthData() || {}
  );
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const access_token = authData.access_token ?? "";
  const userTimezone = getUserTimezone(authData);

  React.useEffect(() => {
    if (contextAuth?.access_token) {
      setAuthData(contextAuth);
      setSessionExpired(false);
    }
  }, [contextAuth]);

  React.useEffect(
    () =>
      subscribeToAuthUpdates((nextAuth) => {
        if (nextAuth) {
          setAuthData(nextAuth);
          setSessionExpired(false);
        }
      }),
    []
  );

  const handleSessionExpired = React.useCallback(async (): Promise<boolean> => {
    if (embedded) {
      const recovered = await recoverAuth();
      if (recovered?.access_token) {
        setSessionExpired(false);
        return true;
      }
      setSessionExpired(true);
      toast.error(BRANDING.sessionExpiredRefreshPageSignIn, {
        autoClose: false,
      });
      return false;
    }

    clearAuthData();
    navigate("/login");
    return false;
  }, [embedded, navigate]);

  const [deliveryAreaData, setDeliveryAreaData] = useState<any>([]);
  const [pickingOrdersData, setPickingOrdersData] = useState<any>([]);
  const [retrieveDriversData, setRetrieveDriversData] = useState<any>(() =>
    getInitialDeliveryPartnersState().drivers
  );
  const [retrieveRoutesData, setRetrieveRoutesData] = useState<any>([]);
  const [retrieveDepotsData, setRetrieveDepotsData] = useState<any>(() =>
    getInitialDeliveryPartnersState().depots
  );

  const [isBoardBusy, setIsBoardBusy] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assignmentFilter, setAssignmentFilter] =
    useState<AssignmentFilter>("unassigned");
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(() => {
    const { start } = getDateRangeForPreset(
      "current_month",
      getUserTimezone(getStoredAuthData())
    );
    return start;
  });
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(() => {
    const { end } = getDateRangeForPreset(
      "current_month",
      getUserTimezone(getStoredAuthData())
    );
    return end;
  });
  const [dateRangePreset, setDateRangePreset] =
    useState<DateRangePresetId | "">("current_month");

  const [knowledgePanelOpen, setKnowledgePanelOpen] = React.useState(false);
  const [openAssignRouteModal, setOpenAssignRouteModal] = React.useState(false);
  const [isAssigningRoute, setIsAssigningRoute] = useState(false);
  const [isRefreshingAssignData, setIsRefreshingAssignData] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [expandedEmptyColumnIds, setExpandedEmptyColumnIds] = useState<
    (number | string)[]
  >([]);
  const [previewStages, setPreviewStages] = useState<BoardStage[] | null>(null);
  const [activeDragItem, setActiveDragItem] = useState<any | null>(null);
  const [activeDragStageColor, setActiveDragStageColor] = useState("#808080");
  const [pendingMove, setPendingMove] =
    useState<PendingDeliveryAreaMove | null>(null);
  const [isUpdatingDeliveryArea, setIsUpdatingDeliveryArea] = useState(false);

  const dragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    })
  );

  const previewStagesRef = useRef<BoardStage[] | null>(null);
  const dragSourceStageIdRef = useRef<number | string | null>(null);
  const lastOverIdRef = useRef<number | string | null>(null);
  const hoveredColumnIdRef = useRef<number | string | null>(null);
  const [hoveredColumnId, setHoveredColumnId] = useState<number | string | null>(
    null
  );

  const boardCollisionDetection = useCallback<CollisionDetection>((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    return closestCenter(args);
  }, []);

  const boardScrollRef = useRef<HTMLDivElement>(null);
  const boardContentRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const isDraggingThumbRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const pickingFetchRequestRef = useRef(0);
  const isFetchingOrdersRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollThumb, setScrollThumb] =
    useState<ScrollThumbMetrics>(DEFAULT_SCROLL_THUMB);

  const updateScrollRail = () => {
    const board = boardScrollRef.current;
    if (!board) {
      return;
    }

    const maxScroll = board.scrollWidth - board.clientWidth;
    const hasOverflow = maxScroll > 4;
    setCanScrollLeft(board.scrollLeft > 4);
    setCanScrollRight(board.scrollLeft < maxScroll - 4);

    if (!hasOverflow) {
      setScrollThumb(DEFAULT_SCROLL_THUMB);
      return;
    }

    const widthPct = Math.max(
      (board.clientWidth / board.scrollWidth) * 100,
      8
    );
    const leftPct =
      (board.scrollLeft / maxScroll) * Math.max(100 - widthPct, 0);

    setScrollThumb({
      widthPct,
      leftPct,
      hasOverflow: true,
    });
  };

  const syncBoardToRail = () => {
    updateScrollRail();
  };

  const setBoardScrollLeft = (nextScrollLeft: number) => {
    const board = boardScrollRef.current;
    if (!board) {
      return;
    }
    const maxScroll = board.scrollWidth - board.clientWidth;
    board.scrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScroll);
    updateScrollRail();
  };

  const scrollBoardBy = (delta: number) => {
    const board = boardScrollRef.current;
    if (!board) {
      return;
    }
    setBoardScrollLeft(board.scrollLeft + delta);
  };

  const handleScrollTrackClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    const board = boardScrollRef.current;
    const track = scrollTrackRef.current;
    if (!board || !track || !scrollThumb.hasOverflow) {
      return;
    }

    const rect = track.getBoundingClientRect();
    const clickRatio = (event.clientX - rect.left) / rect.width;
    const maxScroll = board.scrollWidth - board.clientWidth;
    setBoardScrollLeft(clickRatio * maxScroll);
  };

  const handleScrollThumbMouseDown = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const board = boardScrollRef.current;
    if (!board || !scrollThumb.hasOverflow) {
      return;
    }

    isDraggingThumbRef.current = true;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = board.scrollLeft;
  };

  const isColumnExpanded = (stage: any) =>
    stage?.items?.length > 0 || expandedEmptyColumnIds.includes(stage?.id);

  const toggleEmptyColumn = useCallback((stageId: number | string) => {
    setExpandedEmptyColumnIds((prev) =>
      prev.includes(stageId)
        ? prev.filter((id) => id !== stageId)
        : [...prev, stageId]
    );
  }, []);

  const filteredOrders = useMemo(() => {
    let filtered = Array.isArray(pickingOrdersData) ? pickingOrdersData : [];
    const query = debouncedSearch.trim().toLowerCase();

    if (query) {
      filtered = filtered.filter((item: any) =>
        item?.name?.toLowerCase().includes(query)
      );
    }

    if (assignmentFilter === "unassigned") {
      filtered = filtered.filter((item: any) => !isPickingAssigned(item));
    } else if (assignmentFilter === "assigned") {
      filtered = filtered.filter((item: any) => isPickingAssigned(item));
    }

    return filtered;
  }, [pickingOrdersData, debouncedSearch, assignmentFilter]);

  const pickingOrdersTableData = useMemo(() => {
    const areas = Array.isArray(deliveryAreaData) ? deliveryAreaData : [];
    if (areas.length === 0) {
      return [];
    }
    return normalizeBoardStages(
      buildPickingOrdersTableData(areas, filteredOrders)
    );
  }, [deliveryAreaData, filteredOrders]);

  const selectedIdSet = useMemo(
    () => new Set(selectedIds),
    [selectedIds]
  );

  const handleToggleItem = useCallback((itemId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const has = prev.includes(itemId);
      if (checked === has) {
        return prev;
      }
      if (checked) {
        return [...prev, itemId];
      }
      return prev.filter((id) => id !== itemId);
    });
  }, []);

  const handleToggleColumn = useCallback((itemIds: number[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of itemIds) {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return Array.from(next);
    });
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const isSearchPending = searchInput !== debouncedSearch;

  const sortedStages = useMemo(() => {
    const isExpanded = (stage: any) =>
      stage?.items?.length > 0 || expandedEmptyColumnIds.includes(stage?.id);

    return [...(pickingOrdersTableData || [])].sort((a: any, b: any) => {
      const aExpanded = isExpanded(a);
      const bExpanded = isExpanded(b);
      if (aExpanded !== bExpanded) {
        return aExpanded ? -1 : 1;
      }
      return 0;
    });
  }, [pickingOrdersTableData, expandedEmptyColumnIds]);

  const displayStages = useMemo(
    () => normalizeBoardStages(previewStages ?? sortedStages),
    [previewStages, sortedStages]
  );

  const resetDragState = useCallback(() => {
    previewStagesRef.current = null;
    dragSourceStageIdRef.current = null;
    lastOverIdRef.current = null;
    hoveredColumnIdRef.current = null;
    setHoveredColumnId(null);
    setPreviewStages(null);
    setActiveDragItem(null);
  }, []);

  const resolveDropStageId = useCallback(
    (
      overId: number | string | null | undefined,
      stages: BoardStage[]
    ): number | string | null => {
      if (overId == null) {
        return hoveredColumnIdRef.current;
      }

      return findContainerForOver(stages, overId);
    },
    []
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const item = event.active.data.current?.item;
    const stageId = event.active.data.current?.stageId;
    if (!item || stageId === undefined) {
      return;
    }

    const sourceStage = sortedStages.find((stage) =>
      stageIdsEqual(stage.id, stageId)
    );

    dragSourceStageIdRef.current = stageId;
    lastOverIdRef.current = null;
    hoveredColumnIdRef.current = null;

    setActiveDragItem(item);
    setActiveDragStageColor(sourceStage?.color || "#808080");
    setHoveredColumnId(null);
  }, [sortedStages]);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;
      if (!over) {
        return;
      }

      lastOverIdRef.current = over.id;

      const overContainer = findContainerForOver(sortedStages, over.id);
      if (
        overContainer === null ||
        stageIdsEqual(overContainer, hoveredColumnIdRef.current)
      ) {
        return;
      }

      hoveredColumnIdRef.current = overContainer;
      setHoveredColumnId(overContainer);
    },
    [sortedStages]
  );

  const handleDragCancel = useCallback(() => {
    resetDragState();
  }, [resetDragState]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const item = active.data.current?.item;
      const fromStageId = dragSourceStageIdRef.current;

      setActiveDragItem(null);
      setHoveredColumnId(null);
      hoveredColumnIdRef.current = null;

      if (!item || fromStageId == null) {
        resetDragState();
        return;
      }

      const overId = over?.id ?? lastOverIdRef.current;
      const toStageId = resolveDropStageId(overId, sortedStages);

      if (toStageId === null || stageIdsEqual(toStageId, fromStageId)) {
        resetDragState();
        return;
      }

      const fromStage = sortedStages.find((stage) =>
        stageIdsEqual(stage.id, fromStageId)
      );
      const toStage = sortedStages.find((stage) =>
        stageIdsEqual(stage.id, toStageId)
      );

      const preview = movePickingBetweenStages(
        cloneBoardStages(sortedStages),
        active.id,
        columnDroppableId(toStageId)
      );

      previewStagesRef.current = preview;
      setPreviewStages(preview);

      setPendingMove({
        picking: item,
        fromStageId,
        fromStageTitle: fromStage?.title || "Unknown",
        toStageId,
        toStageTitle: toStage?.title || "Unknown",
      });
    },
    [resetDragState, resolveDropStageId, sortedStages]
  );

  const handleCancelMoveDeliveryArea = useCallback(() => {
    if (isUpdatingDeliveryArea) {
      return;
    }
    setPendingMove(null);
    resetDragState();
  }, [isUpdatingDeliveryArea, resetDragState]);

  const handleConfirmMoveDeliveryArea = useCallback(async () => {
    if (!pendingMove || !access_token || isUpdatingDeliveryArea) {
      return;
    }

    const {
      picking,
      fromStageId,
      fromStageTitle,
      toStageId,
      toStageTitle,
    } = pendingMove;
    const deliveryAreaValue =
      toStageId === "none" ? false : (toStageId as number);

    const buildDeliveryAreaField = (stageId: number | string, title: string) => {
      if (stageId === "none") {
        return false;
      }
      return { id: stageId, name: title };
    };

    const previousDeliveryArea = picking.delivery_area;

    setIsUpdatingDeliveryArea(true);
    setPickingOrdersData((prev: any[]) =>
      (Array.isArray(prev) ? prev : []).map((record) =>
        record.id === picking.id
          ? {
              ...record,
              delivery_area: buildDeliveryAreaField(toStageId, toStageTitle),
            }
          : record
      )
    );
    setPendingMove(null);
    previewStagesRef.current = null;
    setPreviewStages(null);

    try {
      await updatePickingDeliveryArea(
        picking.id,
        deliveryAreaValue,
        access_token
      );
      toast.success(`Moved ${picking.name} to ${toStageTitle}.`);
    } catch (error) {
      const result = await handleApiError(error, { embedded, silent: true });
      if (!result.recovered) {
        setPickingOrdersData((prev: any[]) =>
          (Array.isArray(prev) ? prev : []).map((record) =>
            record.id === picking.id
              ? {
                  ...record,
                  delivery_area:
                    previousDeliveryArea ??
                    buildDeliveryAreaField(fromStageId, fromStageTitle),
                }
              : record
          )
        );
        notifyApiError(error, "Could not update delivery area.");
      }
    } finally {
      setIsUpdatingDeliveryArea(false);
    }
  }, [
    pendingMove,
    access_token,
    isUpdatingDeliveryArea,
    handleSessionExpired,
  ]);

  const isDragDisabled =
    isBoardBusy || isInitialLoading || Boolean(pendingMove);

  useEffect(() => {
    if (pendingMove || activeDragItem) {
      return;
    }
    previewStagesRef.current = null;
    setPreviewStages(null);
  }, [debouncedSearch, assignmentFilter, fromDate, toDate, pendingMove, activeDragItem]);

  const selectedIdsByStage = useMemo(() => {
    const map = new Map<number | string, number[]>();
    for (const stage of displayStages) {
      const items = stage.items ?? [];
      const ids = items
        .filter((item: any) => selectedIdSet.has(item.id))
        .map((item: any) => item.id as number);
      map.set(stage.id, ids.length > 0 ? ids : EMPTY_SELECTED_IDS);
    }
    return map;
  }, [displayStages, selectedIdSet]);

  const fetchPickingOrdersWithFilters = async (filters: unknown[]) => {
    if (!access_token || sessionExpired) {
      return [];
    }

    try {
      const parsed = await odooApiFetch<{ results?: unknown[] }>(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/stock.picking/?filters=${encodeURIComponent(
          serializeOdooFilters(filters as unknown[][])
        )}`,
        access_token
      );
      return extractPickingResults(parsed);
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        const recovered = await handleSessionExpired();
        if (recovered) {
          return [];
        }
      }
      throw error;
    }
  };

  const fetchPickingOrdersForDateRange = async (
    from: dayjs.Dayjs,
    to: dayjs.Dayjs
  ) => {
    const normalizedFrom = from.startOf("day");
    const normalizedTo = to.startOf("day");
    const requestId = ++pickingFetchRequestRef.current;

    isFetchingOrdersRef.current = true;
    setIsBoardBusy(true);

    try {
      const results = await fetchPickingOrdersWithFilters(
        buildPickingFilters({
          startDate: normalizedFrom,
          endDate: normalizedTo,
          timezone: userTimezone,
        })
      );

      if (requestId !== pickingFetchRequestRef.current) {
        return;
      }

      setPickingOrdersData(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error(error);
      if (requestId === pickingFetchRequestRef.current) {
        setPickingOrdersData([]);
        await handleApiError(error, {
          embedded,
          fallbackMessage: "Could not load orders.",
        });
        isFetchingOrdersRef.current = false;
        setIsBoardBusy(false);
      }
    } finally {
      if (requestId === pickingFetchRequestRef.current) {
        isFetchingOrdersRef.current = false;
      }
    }
  };

  const refreshPickingOrdersForCurrentView = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    const from = dayjs(fromDate).startOf("day");
    const to = dayjs(toDate).startOf("day");
    await fetchPickingOrdersForDateRange(from, to);
  };

  const getDeliveryAreaApi = async () => {
    if (!access_token || sessionExpired) {
      return;
    }

    try {
      const parsed = await odooApiFetch<{ results?: unknown[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.area`,
        access_token
      );
      setDeliveryAreaData(extractOdooResults(parsed));
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        await handleSessionExpired();
        return;
      }
      console.error(error);
    }
  };

  const getRetrieveDeliveryPartners = async () => {
    if (!access_token || sessionExpired) {
      return;
    }

    try {
      const parsed = await odooApiFetch<{ count?: number; results?: DeliveryPartner[] }>(
        buildDeliveryPartnersUrl(),
        access_token
      );

      const { drivers, depots } = splitDeliveryPartners(extractOdooResults(parsed));

      setRetrieveDriversData(drivers);
      setRetrieveDepotsData(depots);
      saveDeliveryPartnersToStorage(drivers, depots);
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        await handleSessionExpired();
        return;
      }
      console.error(error);
    }
  };

  const getRetrieveRoutes = async () => {
    if (!access_token || sessionExpired) {
      return;
    }

    try {
      const parsed = await odooApiFetch<{ results?: unknown[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.route?filters=${encodeURIComponent(
          serializeOdooFilters([["state", "in", ["draft", "assigned"]]])
        )}&include_fields=${encodeURIComponent(
          "['id','name','display_name','state','picking_count','total_load_count',('stop_line_ids',('load_count',))]"
        )}`,
        access_token
      );
      setRetrieveRoutesData(extractOdooResults(parsed));
      localStorage.setItem("deliveryRoutes", JSON.stringify(parsed));
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        await handleSessionExpired();
        return;
      }
      console.error(error);
    }
  };

  const handleOpenAssignRouteModal = () => {
    if (selectedIds?.length === 0) {
      toast.error("Please select the data");
      return;
    }

    setOpenAssignRouteModal(true);
  };

  const handleRefreshAssignRouteData = async () => {
    if (!access_token || sessionExpired || isRefreshingAssignData) {
      return;
    }

    setIsRefreshingAssignData(true);
    try {
      await Promise.all([getRetrieveRoutes(), getRetrieveDeliveryPartners()]);
      toast.success("Routes and partners updated.");
    } catch {
      toast.error("Could not refresh routes and partners.");
    } finally {
      setIsRefreshingAssignData(false);
    }
  };

  const handleDashboardRefresh = async () => {
    if (!access_token || sessionExpired) {
      return;
    }

    setIsBoardBusy(true);
    try {
      await getRetrieveDeliveryPartners();
      await refreshPickingOrdersForCurrentView();
    } catch (error) {
      console.error(error);
      toast.error("Could not refresh dashboard data.");
      setIsBoardBusy(false);
    }
  };

  useEffect(() => {
    if (!access_token || sessionExpired) {
      return;
    }

    let cancelled = false;

    const loadInitialData = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([
          getDeliveryAreaApi(),
          getRetrieveRoutes(),
        ]);
      } finally {
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      }
    };

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [access_token, sessionExpired]);

  useEffect(() => {
    if (isInitialLoading || !fromDate || !toDate || !access_token || sessionExpired) {
      return;
    }

    const from = dayjs(fromDate).startOf("day");
    const to = dayjs(toDate).startOf("day");

    setIsBoardBusy(true);
    void fetchPickingOrdersForDateRange(from, to);
  }, [fromDate, toDate, isInitialLoading, access_token, sessionExpired, userTimezone]);

  const handleFromDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) {
      return;
    }
    setIsBoardBusy(true);
    const nextFrom = value.startOf("day");
    setDateRangePreset("");
    setFromDate(nextFrom);
    if (toDate && nextFrom.isAfter(dayjs(toDate), "day")) {
      setToDate(nextFrom);
    }
  };

  const handleToDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) {
      return;
    }
    setIsBoardBusy(true);
    const nextTo = value.startOf("day");
    setDateRangePreset("");
    setToDate(nextTo);
    if (fromDate && nextTo.isBefore(dayjs(fromDate), "day")) {
      setFromDate(nextTo);
    }
  };

  const handleDateRangePresetChange = (preset: DateRangePresetId) => {
    setIsBoardBusy(true);
          setSelectedIds([]);
    previewStagesRef.current = null;
    setPreviewStages(null);
    const { start, end } = getDateRangeForPreset(preset, userTimezone);
    setDateRangePreset(preset);
    setFromDate(start);
    setToDate(end);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleAssignmentFilterChange = (value: AssignmentFilter) => {
    setAssignmentFilter(value);
    setSelectedIds([]);
  };

  const getAssignRouteApiCall = async (
    formValues: AssignRouteFormValues,
    picking_ids: number[]
  ) => {
    if (isAssigningRoute) {
      return;
    }

    setIsAssigningRoute(true);

    try {
      if (formValues.driverId === "") {
        toast.error("Please select a driver.");
        return;
      }
      if (formValues.mode === "existing" && formValues.routeId === "") {
        toast.error("Please select a route.");
        return;
      }

      const data = await assignPickingsToRoute(
        buildAssignPickingsPayload(formValues, picking_ids),
        access_token
      );

      toast.success(data?.message || "Route assigned successfully!", {
        autoClose: 3000,
      });
      setSelectedIds([]);
      await getRetrieveRoutes();
      await refreshPickingOrdersForCurrentView();
      setOpenAssignRouteModal(false);
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Failed to assign route.",
      });
    } finally {
      setIsAssigningRoute(false);
    }
  };

  const handleAssignRoute = async (formValues: AssignRouteFormValues) => {
    if (isAssigningRoute) {
      return;
    }

    if (selectedIds?.length === 0) {
      toast.error("Please select at least one picking order.");
      return;
    }

    await getAssignRouteApiCall(formValues, selectedIds);
  };

  const handleActionChange = (e) => {
    console.log(e?.target?.value);
    if (e?.target?.value === "assignRoute" && selectedIds?.length !== 0) {
      setOpenAssignRouteModal(true);
    } else {
      toast.error("Please select the data");
    }
  };

  const isBoardLoading = isInitialLoading || isBoardBusy;
  const isHeaderBusy = isBoardBusy || isSearchPending;

  useEffect(() => {
    if (isInitialLoading || isFetchingOrdersRef.current) {
      return;
    }
    setIsBoardBusy(false);
  }, [pickingOrdersTableData, isInitialLoading]);

  useLayoutEffect(() => {
    updateScrollRail();
    const board = boardScrollRef.current;
    const content = boardContentRef.current;
    if (!board || !content) {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateScrollRail();
    });
    observer.observe(content);
    observer.observe(board);
    window.addEventListener("resize", updateScrollRail);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateScrollRail);
    };
  }, [sortedStages, isBoardLoading, isInitialLoading, embedded]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingThumbRef.current) {
        return;
      }

      const board = boardScrollRef.current;
      const track = scrollTrackRef.current;
      if (!board || !track) {
        return;
      }

      const maxScroll = board.scrollWidth - board.clientWidth;
      if (maxScroll <= 0) {
        return;
      }

      const trackWidth = track.clientWidth;
      const thumbWidthPx = (scrollThumb.widthPct / 100) * trackWidth;
      const draggableWidth = Math.max(trackWidth - thumbWidthPx, 1);
      const deltaX = event.clientX - dragStartXRef.current;
      const scrollDelta = (deltaX / draggableWidth) * maxScroll;
      setBoardScrollLeft(dragStartScrollLeftRef.current + scrollDelta);
    };

    const handleMouseUp = () => {
      isDraggingThumbRef.current = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [scrollThumb.widthPct]);

  if (!access_token) {
          return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f6fa",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "#7ec597" }} />
        <Typography sx={{ color: "#625a57" }}>Loading session...</Typography>
      </Box>
    );
  }

  return (
      <Box
        sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
          bgcolor: "#f5f6fa",
          overflow: "hidden",
      }}
    >
      {sessionExpired && (
        <Box
          sx={{
            flexShrink: 0,
            mx: { xs: 1.5, sm: 2 },
            mt: embedded ? 1 : { xs: 2, sm: 3 },
            px: 2,
            py: 1.25,
            borderRadius: "10px",
            bgcolor: "#fdecea",
            color: "#b42318",
            border: "1px solid #fecdca",
          }}
        >
          <Typography variant="body2">
            {BRANDING.apiSessionExpired}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, sm: 2 },
          pt: embedded ? 1 : { xs: 1.5, sm: 2 },
          pb: 1.25,
        }}
      >
        <Paper elevation={0} sx={HEADER_PAPER_SX}>
          <Box sx={HEADER_TOOLBAR_SX}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: 152 },
                  flexShrink: 0,
                  ...FILTER_FIELD_SX,
                }}
              >
                <InputLabel id="date-range-preset-label">Date range</InputLabel>
                <Select
                  labelId="date-range-preset-label"
                  id="date-range-preset"
                  label="Date range"
                  value={dateRangePreset}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Custom";
                    }
                    return (
                      DATE_RANGE_PRESET_OPTIONS.find(
                        (option) => option.id === selected
                      )?.label ?? "Custom"
                    );
                  }}
                  onChange={(event) => {
                    handleDateRangePresetChange(
                      event.target.value as DateRangePresetId
                    );
                  }}
                >
                  {DATE_RANGE_PRESET_OPTIONS.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

                      <DatePicker
                label="From"
                format={DATE_DISPLAY_FORMAT}
                value={fromDate}
                onChange={handleFromDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: { xs: "100%", sm: 150 },
                      flexShrink: 0,
                      ...FILTER_FIELD_SX,
                    },
                  },
                }}
              />
              <DatePicker
                label="To"
                format={DATE_DISPLAY_FORMAT}
                value={toDate}
                minDate={fromDate ?? undefined}
                onChange={handleToDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: { xs: "100%", sm: 150 },
                      flexShrink: 0,
                      ...FILTER_FIELD_SX,
                    },
                  },
                }}
              />
                  </LocalizationProvider>

            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                height: 32,
                bgcolor: "#e5e9ef",
                flexShrink: 0,
              }}
            />

                  <TextField
              id="orders-search"
                    label="Search"
                    variant="outlined"
              size="small"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 280, md: 300 },
                flexShrink: 0,
                ...FILTER_FIELD_SX,
              }}
              InputProps={{
                endAdornment:
                  isHeaderBusy && !isInitialLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} sx={{ color: "#7ec597" }} />
                    </InputAdornment>
                  ) : undefined,
              }}
            />

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: 168 },
                flexShrink: 0,
                ...FILTER_FIELD_SX,
              }}
            >
              <InputLabel id="assignment-filter-label">Assignment</InputLabel>
                    <Select
                labelId="assignment-filter-label"
                id="assignment-filter"
                label="Assignment"
                value={assignmentFilter}
                onChange={(e) => {
                  handleAssignmentFilterChange(
                    e.target.value as AssignmentFilter
                  );
                }}
              >
                <MenuItem value="unassigned">Unassigned only</MenuItem>
                <MenuItem value="assigned">Assigned only</MenuItem>
                <MenuItem value="all">All orders</MenuItem>
                    </Select>
                  </FormControl>

                {selectedIds?.length !== 0 && (
                    <Button
                size="small"
                      variant="contained"
                      sx={{
                  height: 40,
                  px: 2,
                  flexShrink: 0,
                        backgroundColor: "#7ec597",
                  ":hover": { backgroundColor: "#6db486" },
                        textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                }}
                onClick={handleOpenAssignRouteModal}
              >
                Assign Route
                    </Button>
            )}

            <Box sx={{ flex: 1, minWidth: 8, display: { xs: "none", lg: "block" } }} />

            {isHeaderBusy && !isInitialLoading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#5fa67a",
                  flexShrink: 0,
                  px: 0.5,
                }}
              >
                <CircularProgress size={18} sx={{ color: "#7ec597" }} />
                <Typography
                  variant="caption"
                  sx={{ display: { xs: "none", sm: "block" }, whiteSpace: "nowrap" }}
                >
                  Updating...
                </Typography>
              </Box>
            )}

            <KnowledgeButton
              active={knowledgePanelOpen}
              onClick={() => setKnowledgePanelOpen(true)}
            />

            <IconButton
              aria-label="Refresh dashboard"
              size="small"
              disabled={isBoardBusy && !isInitialLoading}
              sx={HEADER_ICON_BUTTON_SX}
                  onClick={() => {
                void handleDashboardRefresh();
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>

            {!embedded && (
              <IconButton
                aria-label="Sign out"
                size="small"
                sx={HEADER_ICON_BUTTON_SX}
                  onClick={() => {
                    navigate("/login");
                  clearAuthData();
                  }}
                >
                <ExitToAppIcon fontSize="small" />
              </IconButton>
            )}
              </Box>
        </Paper>
          </Box>

            <Box
              sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          px: { xs: 1.5, sm: 2 },
          pb: embedded ? 1 : 1.5,
                display: "flex",
          flexDirection: "column",
              }}
            >
          {isBoardLoading && (
              <Backdrop
                sx={{
                color: "#7ec597",
                  zIndex: (theme) => theme.zIndex.drawer + 10,
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(245, 246, 250, 0.72)",
                flexDirection: "column",
                gap: 1.5,
                }}
              open={isBoardLoading}
              >
                <CircularProgress color="inherit" />
              {!isInitialLoading && (
                <Typography variant="body2" sx={{ color: "#5fa67a" }}>
                  Loading orders...
                </Typography>
              )}
              </Backdrop>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.75,
              flexShrink: 0,
              px: 0.5,
              minHeight: 24,
            }}
          >
            <Typography
              variant="caption"
                  sx={{
                color: "#737372",
                flexShrink: 0,
                fontSize: embedded ? "0.7rem" : "0.75rem",
                display: { xs: scrollThumb.hasOverflow ? "none" : "block", sm: "block" },
              }}
            >
              Scroll horizontally to view all delivery areas
                    </Typography>
            {scrollThumb.hasOverflow && (
                  <Box
                    sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <IconButton
                  size="small"
                  disabled={!canScrollLeft}
                  onClick={() => scrollBoardBy(-320)}
                            sx={{
                    p: 0.25,
                    color: "#5fa67a",
                    flexShrink: 0,
                  }}
                  aria-label="Scroll delivery areas left"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Box
                  ref={scrollTrackRef}
                  onClick={handleScrollTrackClick}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 999,
                    bgcolor: "#dde3e8",
                              position: "relative",
                    cursor: "pointer",
                    minWidth: 48,
                            }}
                          >
                            <Box
                    onMouseDown={handleScrollThumbMouseDown}
                              sx={{
                                position: "absolute",
                      top: 0,
                      left: `${scrollThumb.leftPct}%`,
                      width: `${scrollThumb.widthPct}%`,
                                height: "100%",
                      borderRadius: 999,
                      bgcolor: "#7ec597",
                      cursor: "grab",
                      "&:hover": {
                        bgcolor: "#6db486",
                      },
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  disabled={!canScrollRight}
                  onClick={() => scrollBoardBy(320)}
                  sx={{
                    p: 0.25,
                    color: "#5fa67a",
                    flexShrink: 0,
                  }}
                  aria-label="Scroll delivery areas right"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
                                  <Box
                                    sx={{
              flex: 1,
              minHeight: 0,
                                      display: "flex",
              flexDirection: "column",
              border: "1px solid #e5e9ef",
              borderRadius: "14px",
              bgcolor: "#f8fafc",
              overflow: "hidden",
            }}
          >
            <Box
              ref={boardScrollRef}
              onScroll={syncBoardToRail}
              className="delivery-board-scroll"
                                      sx={{
                flex: 1,
                minHeight: 0,
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Box
                ref={boardContentRef}
                                      sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  gap: 1.5,
                  height: "100%",
                  minWidth: "min-content",
                  alignItems: "stretch",
                  p: 1.5,
                  pb: 1.5,
                  pr: 0.5,
                }}
              >
            {sortedStages.length !== 0 &&
              !isInitialLoading &&
              (
                <DndContext
                  sensors={dragSensors}
                  collisionDetection={boardCollisionDetection}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  {displayStages.map((stage: any) => (
                    <DeliveryBoardColumn
                      key={stage.id}
                      stage={stage}
                      collapsed={!isColumnExpanded(stage)}
                      userTimezone={userTimezone}
                      selectedIdsInStage={
                        selectedIdsByStage.get(stage.id) ?? EMPTY_SELECTED_IDS
                      }
                      onToggleCollapse={toggleEmptyColumn}
                      onToggleItem={handleToggleItem}
                      onToggleColumn={handleToggleColumn}
                      dragDisabled={isDragDisabled}
                      dropTargetStageId={hoveredColumnId}
                    />
                  ))}
                  <DragOverlay
                    style={{ pointerEvents: "none" }}
                    dropAnimation={{
                      duration: 220,
                      easing: "cubic-bezier(0.18, 0.67, 0.32, 1.02)",
                    }}
                  >
                    {activeDragItem ? (
                      <Box
              sx={{
                          transform: "scale(1.02)",
                          boxShadow: "0 16px 36px rgba(15, 23, 42, 0.2)",
                          borderRadius: "12px",
                          cursor: "grabbing",
                          pointerEvents: "none",
                        }}
                      >
                        <DeliveryPickingCard
                          item={activeDragItem}
                          stageColor={activeDragStageColor}
                          userTimezone={userTimezone}
                          isSelected={selectedIdSet.has(activeDragItem.id)}
                          onToggle={() => undefined}
                    />
                  </Box>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            {pickingOrdersTableData?.length === 0 && !isBoardLoading && (
                <Box
                  sx={{
                  flex: "1 1 300px",
                  minWidth: 300,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  No delivery areas to display
                      </Typography>
                </Box>
            )}
        </Box>
      </Box>
          </Box>
      </Box>
      <KnowledgePanel
        open={knowledgePanelOpen}
        onClose={() => setKnowledgePanelOpen(false)}
      />

      {openAssignRouteModal && (
        <AssignRouteModal
          openModal={openAssignRouteModal}
          isSubmitting={isAssigningRoute}
          isRefreshing={isRefreshingAssignData}
          routes={retrieveRoutesData}
          drivers={retrieveDriversData}
          depots={retrieveDepotsData}
          onRefresh={handleRefreshAssignRouteData}
          handleClose={(e, reason) => {
            if (isAssigningRoute) {
              return;
            }
            if (reason && reason === "backdropClick") {
              return;
            }
            setOpenAssignRouteModal(false);
          }}
          handleAssignRoute={handleAssignRoute}
        />
      )}
      <MoveDeliveryAreaModal
        open={Boolean(pendingMove)}
        pendingMove={pendingMove}
        isSubmitting={isUpdatingDeliveryArea}
        userTimezone={userTimezone}
        embedded={embedded}
        onConfirm={handleConfirmMoveDeliveryArea}
        onCancel={handleCancelMoveDeliveryArea}
      />
    </Box>
  );
};
